import { memo, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { BasicDialog } from "ui/molecules/Dialog";
import KycSettingsCard from "../KycSettingsCard";
import useKycSettingForm from "../KycSettingsCard/useKycSettingForm";

type Props = {
    readonly open: boolean;
    readonly onClose: () => void;
};

const AddKycDialog = ({ open, onClose }: Props) => {
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const {
        formValues,
        fieldErrors,
        error,
        isSubmitting,
        onFieldChange,
        validateForm,
        resetForm,
        handleSubmit
    } = useKycSettingForm({
        onSuccess: () => {
            setIsConfirmOpen(false);
            onClose();
        }
    });

    useEffect(() => {
        if (!open) {
            resetForm();
            setIsConfirmOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) {
        return null;
    }

    const requestConfirm = () => {
        if (!validateForm()) {
            return;
        }
        setIsConfirmOpen(true);
    };

    const closeConfirm = () => {
        if (isSubmitting) return;
        setIsConfirmOpen(false);
    };

    return (
        <>
            <BasicDialog
                isOpen={open}
                onClose={onClose}
                isCanClose={!isSubmitting && !isConfirmOpen}
            >
                <Box sx={{ width: "100%", px: 1, py: 1 }}>
                    <KycSettingsCard
                        mode='create'
                        formValues={formValues}
                        fieldErrors={fieldErrors}
                        error={error}
                        isSubmitting={isSubmitting}
                        onFieldChange={onFieldChange}
                        onSave={requestConfirm}
                        onCancel={onClose}
                    />
                </Box>
            </BasicDialog>

            <BasicDialog
                isOpen={isConfirmOpen}
                onClose={closeConfirm}
                isCanClose={!isSubmitting}
                actionsSlot={(
                    <Stack direction='row' spacing={2}>
                        <LoadingButton
                            variant='contained'
                            loading={isSubmitting}
                            onPointerDown={handleSubmit}
                            data-test-id='pspKycSettings.add.confirm'
                        >
                            Yes, add KYC
                        </LoadingButton>
                        <Button
                            variant='outlined'
                            onPointerDown={closeConfirm}
                            disabled={isSubmitting}
                            data-test-id='pspKycSettings.add.cancel'
                        >
                            Cancel
                        </Button>
                    </Stack>
                )}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, textAlign: 'center' }}
                    >
                        Are you sure you want to add this PSP KYC setting?
                    </Typography>
                    {formValues.PSP && (
                        <Typography
                            variant='body1'
                            sx={{ mt: 2, textAlign: 'center' }}
                        >
                            “{formValues.PSP}”
                        </Typography>
                    )}
                </Box>
            </BasicDialog>
        </>
    );
};

export default memo(AddKycDialog);
